import React, { useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import {
  Box,
  Flex,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from 'chart.js';
import { Doughnut, Pie, Bar } from 'react-chartjs-2';
import DashboardController from '../../../../controller/admin/dashboard';
import RekapKepesantrenanController, {
  useRekapKepesantrenan,
} from '../../../../controller/admin/laporan/rekap_kepesantrenan';
import SidebarWithHeader from '../../../../components/Admin/SidebarWithHeader';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const HIJAU = ['#007E39', '#48BB78', '#68D391', '#9AE6B4', '#C6F6D5', '#276749', '#2F855A', '#38A169', '#48BB78', '#68D391'];

const WrapperMain: React.FC = () => {
  const { fetchRekap, fetchPelatihan, setSelectedPelatihanId } = useRekapKepesantrenan();
  const { rekapData, isLoading, listPelatihan, isLoadingPelatihan, selectedPelatihanId } =
    useRekapKepesantrenan().state;

  useEffect(() => {
    fetchPelatihan();
    fetchRekap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bukan_pesantren = (rekapData?.total ?? 0) - (rekapData?.dari_pesantren ?? 0);

  const doughnutData = {
    labels: ['Dari Pesantren', 'Bukan dari Pesantren'],
    datasets: [{
      data: [rekapData?.dari_pesantren ?? 0, bukan_pesantren],
      backgroundColor: ['#007E39', '#C6F6D5'],
      borderColor: ['#005a29', '#9AE6B4'],
      borderWidth: 1,
    }],
  };

  const peranData = {
    labels: (rekapData?.by_peran ?? []).map(
      (d) => d.peran.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    ),
    datasets: [{
      label: 'Jumlah Peserta',
      data: (rekapData?.by_peran ?? []).map((d) => d.jumlah),
      backgroundColor: HIJAU,
    }],
  };

  const provinsiData = {
    labels: (rekapData?.by_provinsi ?? []).map((d) => d.nama_provinsi),
    datasets: [{
      label: 'Jumlah Peserta',
      data: (rekapData?.by_provinsi ?? []).map((d) => d.jumlah),
      backgroundColor: HIJAU,
    }],
  };

  const mukimItem = rekapData?.by_mukim ?? [];
  const mukimMap: Record<number, number> = {};
  mukimItem.forEach((d) => { mukimMap[d.status_mukim] = d.jumlah; });
  const mukimData = {
    labels: ['Mukim', 'Tidak Mukim'],
    datasets: [{
      data: [mukimMap[1] ?? 0, mukimMap[0] ?? 0],
      backgroundColor: ['#007E39', '#48BB78'],
      borderColor: ['#005a29', '#2F855A'],
      borderWidth: 1,
    }],
  };

  const barOpts = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  const barOptsHorizontal = {
    indexAxis: 'y' as const,
    responsive: true,
    plugins: { legend: { display: false } },
    scales: { x: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  return (
    <SidebarWithHeader title="Rekap Data Kepesantrenan">
      {/* Filter */}
      <Box bg="white" p={4} borderRadius="lg" mb={4} shadow="sm">
        <Flex gap={3} alignItems="center" wrap="wrap">
          <Text fontWeight="semibold" fontSize="sm" minW="fit-content">Filter Pelatihan:</Text>
          <Select
            placeholder={isLoadingPelatihan ? 'Memuat...' : 'Semua Pelatihan'}
            value={selectedPelatihanId}
            onChange={(e) => setSelectedPelatihanId(e.target.value)}
            maxW="420px"
            isDisabled={isLoadingPelatihan}
          >
            {listPelatihan.map((p) => (
              <option key={p.id} value={p.id}>
                {p.judul_pelatihan}
              </option>
            ))}
          </Select>
          {isLoading && <Spinner size="sm" color="green.500" />}
        </Flex>
      </Box>

      {/* Stat cards */}
      {rekapData && (
        <SimpleGrid columns={[2, null, 3]} spacing={4} mb={4}>
          <Box bg="white" p={4} borderRadius="lg" shadow="sm">
            <Stat>
              <StatLabel color="gray.500">Total Peserta</StatLabel>
              <StatNumber color="green.700">{rekapData.total}</StatNumber>
              <StatHelpText>semua pelatihan</StatHelpText>
            </Stat>
          </Box>
          <Box bg="white" p={4} borderRadius="lg" shadow="sm">
            <Stat>
              <StatLabel color="gray.500">Dari Pesantren</StatLabel>
              <StatNumber color="green.700">{rekapData.dari_pesantren}</StatNumber>
              <StatHelpText>
                {rekapData.total > 0
                  ? `${Math.round((rekapData.dari_pesantren / rekapData.total) * 100)}%`
                  : '0%'}
              </StatHelpText>
            </Stat>
          </Box>
          <Box bg="white" p={4} borderRadius="lg" shadow="sm">
            <Stat>
              <StatLabel color="gray.500">Bukan Pesantren</StatLabel>
              <StatNumber color="gray.600">{bukan_pesantren}</StatNumber>
              <StatHelpText>
                {rekapData.total > 0
                  ? `${Math.round((bukan_pesantren / rekapData.total) * 100)}%`
                  : '0%'}
              </StatHelpText>
            </Stat>
          </Box>
        </SimpleGrid>
      )}

      {/* Charts */}
      <SimpleGrid columns={[1, null, 2]} spacing={4}>
        <Box bg="white" p={4} borderRadius="lg" shadow="sm">
          <Text fontWeight="semibold" mb={3} color="gray.700">
            Dari Pesantren vs Tidak
          </Text>
          <Box maxH="280px" display="flex" justifyContent="center">
            <Doughnut data={doughnutData} options={{ responsive: true, maintainAspectRatio: false }} />
          </Box>
        </Box>

        <Box bg="white" p={4} borderRadius="lg" shadow="sm">
          <Text fontWeight="semibold" mb={3} color="gray.700">
            Status Mukim (Peserta dari Pesantren)
          </Text>
          <Box maxH="280px" display="flex" justifyContent="center">
            <Pie data={mukimData} options={{ responsive: true, maintainAspectRatio: false }} />
          </Box>
        </Box>

        <Box bg="white" p={4} borderRadius="lg" shadow="sm">
          <Text fontWeight="semibold" mb={3} color="gray.700">
            Distribusi Peran
          </Text>
          <Bar data={peranData} options={barOptsHorizontal} />
        </Box>

        <Box bg="white" p={4} borderRadius="lg" shadow="sm">
          <Text fontWeight="semibold" mb={3} color="gray.700">
            Top 10 Provinsi
          </Text>
          <Bar data={provinsiData} options={barOpts} />
        </Box>
      </SimpleGrid>
    </SidebarWithHeader>
  );
};

const RekapDataKepesantrenan: React.FC = () => {
  return (
    <DashboardController.Provider>
      <RekapKepesantrenanController.Provider>
        <ToastContainer position="top-center" style={{ width: '60%', marginTop: '5%' }} />
        <WrapperMain />
      </RekapKepesantrenanController.Provider>
    </DashboardController.Provider>
  );
};

export default RekapDataKepesantrenan;
